import { useState } from "react";
import { Modal } from "./Modal";
import { Button } from "./Button";
import { TextField } from "./Field";
import { useT } from "@/contexts/I18nContext";

/**
 * A destructive action, confirmed by typing.
 *
 * In this console the reason is not optional decoration: it is written into the
 * audit log under the name of whoever pressed the button, and a colleague
 * reading it in a year has to be able to understand what happened.
 *
 * The confirmation is a **word or a number the reader has to read off the
 * screen** — the count for a bulk action, the person's name for a deletion. A
 * plain "are you sure" is answered yes by reflex; typing 27 is not something
 * anyone does while thinking about something else.
 *
 * The body always says what is removed and what is not. "Statistics are
 * unaffected" is the sentence an owner needs before deleting a student, and
 * they should not have to ask for it.
 */
export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  titleKey,
  titleParams,
  bodyKey,
  /** What the reader must type, verbatim. */
  expected,
  confirmKey,
  mismatchKey,
  /** Shown when the action is recorded in the audit log with a reason. */
  withReason = false,
  destructive = true,
}: {
  open: boolean;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
  titleKey: string;
  titleParams?: Record<string, unknown>;
  bodyKey: string;
  expected: string;
  confirmKey: string;
  mismatchKey: string;
  withReason?: boolean;
  destructive?: boolean;
}) {
  const t = useT();
  const [typed, setTyped] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const matches = typed.trim() === expected;
  const ready = matches && (!withReason || reason.trim().length > 0);

  const close = () => {
    setTyped("");
    setReason("");
    onClose();
  };

  return (
    <Modal open={open} onClose={close} titleKey={titleKey} titleParams={titleParams}>
      <div className="flex flex-col gap-4">
        <p className="text-sm text-ink-soft">{t(bodyKey, titleParams)}</p>

        <TextField
          labelKey={confirmKey}
          value={typed}
          autoComplete="off"
          errorKey={typed.length > 0 && !matches ? mismatchKey : undefined}
          onChange={(event) => setTyped(event.target.value)}
        />

        {withReason ? (
          <TextField
            labelKey="audit.reason"
            hintKey="audit.reasonHint"
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={close}>
            {t("common.cancel")}
          </Button>
          <Button
            variant={destructive ? "danger" : "primary"}
            busy={busy}
            disabled={!ready}
            onClick={async () => {
              setBusy(true);
              try {
                await onConfirm(reason.trim());
                close();
              } finally {
                setBusy(false);
              }
            }}
          >
            {t("common.confirm")}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
